<script>
import Layout from '@src/router/layouts/main'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import moment from 'moment'
import TipoConta from '@/src/services/tipo-conta'
import ContaPagarReceber from '@/src/services/conta-pagar-receber'
import CategoriaConta from '@/src/services/categoria-conta'
import CentroCusto from '@/src/services/centro-custo'
import produtor from '@/src/services/produtor-rural'

moment.locale('pt-br')

export default {
  components: {
    Layout,
    Loading,
  },
  data() {
    return {
      isLoading: false,
      filtersSidebarOpen: false,
      statusFilter: 'todos',
      statusOptions: [
        { value: 'todos', text: 'Todos os Status' },
        { value: 'suggested', text: 'Sugerido' },
        { value: 'matched', text: 'Conciliado' },
        { value: 'unmatched', text: 'Sem correspondência' },
        { value: 'ignored', text: 'Ignorado' },
      ],
      bankAccounts: [],
      financialAccounts: [],
      statementItems: [],
      reconciliationSummary: null,
      categoriasConta: [],
      centroCusto: [],
      produtores: [],
      selectedBankAccountId: null,
      startDate: moment().startOf('month').format('YYYY-MM-DD'),
      endDate: moment().endOf('month').format('YYYY-MM-DD'),
      manualForm: {
        statementItem: null,
        candidates: [],
        selectedAccountId: null,
      },
      createForm: {
        statementItem: null,
        id_cliente: null,
        id_fornecedor: null,
        id_categoria: null,
        id_centro_custo: null,
        descricao: '',
      },
    }
  },
  computed: {
    bankAccountOptions() {
      return this.bankAccounts.map((item) => ({
        value: item.id,
        text: [item.banco, item.agencia, item.conta, item.tipo]
          .filter(Boolean)
          .join(' - '),
      }))
    },
    selectedBankAccount() {
      return (
        this.bankAccounts.find(
          (item) => String(item.id) === String(this.selectedBankAccountId)
        ) || null
      )
    },
    transactions() {
      return this.statementItems.map((item) => {
        const matchedAccount =
          item.matchedAccount ||
          this.financialAccounts.find((account) => account.id === item.matchedAccountId)
        return {
          ...item,
          matchedAccount,
          erpValue: matchedAccount ? this.accountSignedValue(matchedAccount) : null,
          bankValue: item.amount,
          statusLabel: this.reconciliationStatusLabel(item.status),
          action:
            item.status === 'suggested'
              ? 'confirm'
              : item.status === 'unmatched'
              ? 'create'
              : item.status === 'pending'
              ? 'create'
              : item.status === 'ignored'
              ? 'reopen'
              : 'view',
          highlighted: item.status === 'suggested',
        }
      })
    },
    filteredTransactions() {
      if (this.statusFilter === 'todos') return this.transactions
      return this.transactions.filter((item) => item.status === this.statusFilter)
    },
    summary() {
      if (this.reconciliationSummary) {
        return {
          totalStatementCredits: this.parseStatementAmount(
            this.reconciliationSummary.total_creditos_extrato
          ),
          totalStatementDebits: this.parseStatementAmount(
            this.reconciliationSummary.total_debitos_extrato
          ),
          totalMatchedCredits: this.parseStatementAmount(
            this.reconciliationSummary.total_conciliado_creditos
          ),
          totalMatchedDebits: this.parseStatementAmount(
            this.reconciliationSummary.total_conciliado_debitos
          ),
          pendingItems: Number(this.reconciliationSummary.pendentes || 0),
          matchedItems: Number(this.reconciliationSummary.conciliadas || 0),
          ignoredItems: Number(this.reconciliationSummary.ignoradas || 0),
          difference: this.parseStatementAmount(this.reconciliationSummary.diferenca),
        }
      }
      const totalStatementCredits = this.statementItems
        .filter((item) => item.amount > 0)
        .reduce((total, item) => total + item.amount, 0)
      const totalStatementDebits = this.statementItems
        .filter((item) => item.amount < 0)
        .reduce((total, item) => total + Math.abs(item.amount), 0)
      const matchedItems = this.statementItems.filter(
        (item) => item.status === 'matched'
      )
      const totalMatchedCredits = matchedItems
        .filter((item) => item.amount > 0)
        .reduce((total, item) => total + item.amount, 0)
      const totalMatchedDebits = matchedItems
        .filter((item) => item.amount < 0)
        .reduce((total, item) => total + Math.abs(item.amount), 0)

      return {
        totalStatementCredits,
        totalStatementDebits,
        totalMatchedCredits,
        totalMatchedDebits,
        pendingItems: this.statementItems.filter(
          (item) => item.status === 'pending' || item.status === 'suggested' || item.status === 'unmatched'
        ).length,
        matchedItems: matchedItems.length,
        ignoredItems: this.statementItems.filter((item) => item.status === 'ignored')
          .length,
        difference:
          totalStatementCredits -
          totalStatementDebits -
          (totalMatchedCredits - totalMatchedDebits),
      }
    },
    progressPercent() {
      if (!this.statementItems.length) return 0
      return Math.round((this.summary.matchedItems / this.statementItems.length) * 100)
    },
  },
  mounted() {
    this.loadInitialData()
  },
  methods: {
    async loadInitialData() {
      const token = this.$store.state.auth.currentUser.token
      this.isLoading = true
      try {
        const [accounts, categories, costs, producers] = await Promise.all([
          TipoConta.listBankAccounts(token),
          CategoriaConta.listar(token),
          CentroCusto.listar(token),
          produtor.listar(token, 1, 100),
        ])
        this.bankAccounts = accounts.data.tipoContas || []
        this.categoriasConta = categories.data.categorias || []
        this.centroCusto = costs.data.centros || []
        this.produtores = producers.data.data || []
        if (!this.selectedBankAccountId && this.bankAccounts.length) {
          this.selectedBankAccountId = this.bankAccounts[0].id
          await this.loadFinancialAccounts(false)
        }
      } catch (error) {
        this.showApiError(error, 'Nao foi possivel carregar os dados de conciliacao.')
      } finally {
        this.isLoading = false
      }
    },
    async loadFinancialAccounts(showValidation = true) {
      if (!this.selectedBankAccountId || !this.startDate || !this.endDate) {
        if (showValidation) {
          this.$bvToast.toast('Selecione a conta bancária e o período.', {
            variant: 'warning',
            solid: false,
            noCloseButton: true,
            toaster: 'b-toaster-top-center',
          })
        }
        return
      }

      const token = this.$store.state.auth.currentUser.token
      this.isLoading = true
      try {
        const response = await ContaPagarReceber.listBankReconciliation(token, {
          bankAccountId: this.selectedBankAccountId,
          startDate: this.startDate,
          endDate: this.endDate,
          status: this.statusFilter !== 'todos' ? this.backendStatus(this.statusFilter) : '',
        })
        const data = response.data || {}
        this.reconciliationSummary = data.resumo || null
        this.statementItems = (data.transacoes || []).map(this.normalizeBackendTransaction)
        this.financialAccounts = this.statementItems
          .map((item) => item.matchedAccount)
          .filter(Boolean)
      } catch (error) {
        this.financialAccounts = []
        this.statementItems = []
        this.reconciliationSummary = null
        this.showApiError(error, 'Nao foi possivel carregar a conciliacao bancaria.')
      } finally {
        this.isLoading = false
      }
    },
    triggerFileImport() {
      if (!this.selectedBankAccountId) {
        this.$bvToast.toast('Selecione uma conta bancária antes de importar.', {
          variant: 'warning',
          solid: false,
          noCloseButton: true,
          toaster: 'b-toaster-top-center',
        })
        return
      }
      this.$refs.statementInput.click()
    },
    async handleStatementFile(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      const token = this.$store.state.auth.currentUser.token
      this.isLoading = true
      try {
        await ContaPagarReceber.importBankStatement(token, {
          file,
          bankAccountId: this.selectedBankAccountId,
          startDate: this.startDate,
          endDate: this.endDate,
        })
        this.$bvToast.toast('Extrato importado com sucesso.', {
          variant: 'success',
          solid: false,
          noCloseButton: true,
          toaster: 'b-toaster-top-center',
        })
        await this.loadFinancialAccounts(false)
      } catch (error) {
        this.showApiError(error, 'Nao foi possivel importar o extrato.')
      } finally {
        this.isLoading = false
        event.target.value = ''
      }
    },
    normalizeBackendTransaction(transaction) {
      const amount = this.parseStatementAmount(transaction.valor)
      const type = transaction.tipo || (amount >= 0 ? 'CREDIT' : 'DEBIT')
      const matchedAccount =
        transaction.conta_sugerida ||
        transaction.conta_financeira ||
        transaction.conta ||
        null
      return {
        ...transaction,
        date: transaction.data_movimento || transaction.date,
        description: transaction.descricao || transaction.description || 'Movimento bancario',
        document: transaction.documento || transaction.document,
        amount,
        type,
        status: this.frontStatus(transaction.status),
        matchedAccountId:
          transaction.id_conta_financeira ||
          transaction.matchedAccountId ||
          (matchedAccount ? matchedAccount.id : null),
        matchedAccount,
        score: transaction.score_sugestao || (matchedAccount ? matchedAccount.score : null),
        reason: transaction.motivo_sugestao || (matchedAccount ? matchedAccount.motivo : ''),
      }
    },
    parseDelimitedStatement(content) {
      const lines = content
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean)
      if (!lines.length) return []

      const delimiter = lines[0].includes(';') ? ';' : ','
      const firstColumns = this.splitLine(lines[0], delimiter)
      const hasHeader = firstColumns.some((column) =>
        /data|date|descricao|description|valor|amount/i.test(column)
      )
      const header = hasHeader ? firstColumns.map((item) => item.toLowerCase()) : []
      const dataLines = hasHeader ? lines.slice(1) : lines

      return dataLines
        .map((line, index) => {
          const columns = this.splitLine(line, delimiter)
          const dateValue = this.pickColumn(columns, header, ['data', 'date'], 0)
          const description = this.pickColumn(
            columns,
            header,
            ['descricao', 'descrição', 'description', 'historico', 'histórico'],
            1
          )
          const amountValue = this.pickColumn(
            columns,
            header,
            ['valor', 'amount', 'valor r$', 'vlr'],
            2
          )
          const amount = this.parseStatementAmount(amountValue)
          if (!dateValue || !Number.isFinite(amount)) return null
          return this.normalizeStatementItem({
            id: `csv-${index}-${dateValue}-${amount}`,
            date: dateValue,
            description: description || 'Movimento bancario',
            amount,
            raw: columns,
          })
        })
        .filter(Boolean)
    },
    parseOfx(content) {
      const transactions = content.match(/<STMTTRN>[\s\S]*?(?=<STMTTRN>|<\/BANKTRANLIST>)/gi) || []
      return transactions
        .map((transaction, index) => {
          const date = this.ofxTag(transaction, 'DTPOSTED')
          const amount = this.parseStatementAmount(this.ofxTag(transaction, 'TRNAMT'))
          const description =
            this.ofxTag(transaction, 'MEMO') ||
            this.ofxTag(transaction, 'NAME') ||
            'Movimento bancario'
          const document = this.ofxTag(transaction, 'FITID')
          if (!date || !Number.isFinite(amount)) return null
          return this.normalizeStatementItem({
            id: document || `ofx-${index}-${date}-${amount}`,
            date,
            description,
            amount,
            document,
            raw: transaction,
          })
        })
        .filter(Boolean)
    },
    ofxTag(content, tag) {
      const match = content.match(new RegExp(`<${tag}>([^<\\r\\n]+)`, 'i'))
      return match ? match[1].trim() : ''
    },
    splitLine(line, delimiter) {
      const regex = new RegExp(`${delimiter}(?=(?:[^"]*"[^"]*")*[^"]*$)`)
      return line.split(regex).map((value) => value.replace(/^"|"$/g, '').trim())
    },
    pickColumn(columns, header, names, fallbackIndex) {
      if (header.length) {
        const index = header.findIndex((column) => names.includes(column))
        if (index >= 0) return columns[index]
      }
      return columns[fallbackIndex]
    },
    normalizeStatementItem(item) {
      const date = this.normalizeDate(item.date)
      const amount = Number(item.amount)
      return {
        ...item,
        date,
        amount,
        type: amount >= 0 ? 'CREDIT' : 'DEBIT',
        matchedAccountId: null,
        status: 'pending',
      }
    },
    normalizeDate(value) {
      const text = String(value || '').trim()
      if (/^\d{8}/.test(text)) {
        return moment(text.substring(0, 8), 'YYYYMMDD').format('YYYY-MM-DD')
      }
      const formats = ['YYYY-MM-DD', 'DD/MM/YYYY', 'DD-MM-YYYY', 'MM/DD/YYYY']
      const parsed = moment(text, formats, true)
      return parsed.isValid() ? parsed.format('YYYY-MM-DD') : text
    },
    parseStatementAmount(value) {
      if (typeof value === 'number') return value
      const text = String(value || '')
        .replace(/R\$\s?/g, '')
        .replace(/\s/g, '')
      const normalized =
        text.includes(',') && text.lastIndexOf(',') > text.lastIndexOf('.')
          ? text.replace(/\./g, '').replace(',', '.')
          : text.replace(/,/g, '')
      const parsed = Number(normalized)
      return Number.isFinite(parsed) ? parsed : NaN
    },
    applySuggestions() {
      if (!this.statementItems.length || !this.financialAccounts.length) return
      const usedAccounts = new Set(
        this.statementItems
          .filter((item) => item.status === 'matched' && item.matchedAccountId)
          .map((item) => item.matchedAccountId)
      )
      this.statementItems = this.statementItems.map((item) => {
        if (item.status === 'matched' || item.status === 'ignored') return item
        const matches = this.financialAccounts.filter((account) => {
          if (usedAccounts.has(account.id)) return false
          const sameType =
            item.type === 'CREDIT'
              ? account.tipo_operacao === 'RECEBER'
              : account.tipo_operacao === 'PAGAR'
          const sameValue =
            Math.abs(Math.abs(item.amount) - this.accountAmount(account)) < 0.01
          const closeDate =
            Math.abs(moment(item.date).diff(moment(account.data_vencimento), 'days')) <=
            3
          const notPaid = account.pago === false
          return sameType && sameValue && closeDate && notPaid
        })
        if (matches.length === 1) {
          return {
            ...item,
            matchedAccountId: matches[0].id,
            status: 'suggested',
          }
        }
        return {
          ...item,
          matchedAccountId: null,
          status: 'unmatched',
          candidates: matches,
        }
      })
    },
    accountAmount(account) {
      return Math.abs(
        this.parseStatementAmount(
          account.valor_final_formatado ||
            account.valor_formatado ||
            account.valor_final ||
            account.valor
        )
      )
    },
    accountSignedValue(account) {
      const value = this.accountAmount(account)
      return account.tipo_operacao === 'PAGAR' ? -value : value
    },
    operationByTransaction(transaction) {
      return transaction.type === 'CREDIT' ? 'RECEBER' : 'PAGAR'
    },
    async confirmReconciliation(transaction, selectedAccount = null) {
      const account = selectedAccount || transaction.matchedAccount
      const accountId =
        (account && account.id) ||
        transaction.matchedAccountId ||
        this.manualForm.selectedAccountId
      if (!accountId) {
        this.$bvToast.toast('Selecione uma conta para conciliar.', {
          variant: 'warning',
          solid: false,
          noCloseButton: true,
          toaster: 'b-toaster-top-center',
        })
        return
      }
      const token = this.$store.state.auth.currentUser.token
      this.isLoading = true
      try {
        await ContaPagarReceber.confirmBankReconciliation(token, transaction.id, {
          id_conta_financeira: accountId,
          tipo_operacao:
            (account && account.tipo_operacao) || this.operationByTransaction(transaction),
          data_pagamento: transaction.date,
        })
        this.$bvModal.hide('modal-manual-reconciliation')
        await this.loadFinancialAccounts(false)
        this.$bvToast.toast('Lançamento conciliado com sucesso.', {
          variant: 'success',
          solid: false,
          noCloseButton: true,
          toaster: 'b-toaster-top-center',
        })
      } catch (error) {
        this.showApiError(error, 'Nao foi possivel conciliar o lancamento.')
      } finally {
        this.isLoading = false
      }
    },
    async ignoreStatementItem(transaction) {
      const token = this.$store.state.auth.currentUser.token
      this.isLoading = true
      try {
        await ContaPagarReceber.ignoreBankReconciliationTransaction(token, transaction.id, {
          motivo: 'Ignorado pelo usuário',
        })
        await this.loadFinancialAccounts(false)
        this.$bvToast.toast('Transação ignorada.', {
          variant: 'success',
          solid: false,
          noCloseButton: true,
          toaster: 'b-toaster-top-center',
        })
      } catch (error) {
        this.showApiError(error, 'Nao foi possivel ignorar a transacao.')
      } finally {
        this.isLoading = false
      }
    },
    async reopenStatementItem(transaction) {
      const token = this.$store.state.auth.currentUser.token
      this.isLoading = true
      try {
        await ContaPagarReceber.reopenBankReconciliationTransaction(token, transaction.id)
        await this.loadFinancialAccounts(false)
        this.$bvToast.toast('Transação reaberta.', {
          variant: 'success',
          solid: false,
          noCloseButton: true,
          toaster: 'b-toaster-top-center',
        })
      } catch (error) {
        this.showApiError(error, 'Nao foi possivel reabrir a transacao.')
      } finally {
        this.isLoading = false
      }
    },
    async openManualModal(transaction) {
      this.manualForm = {
        statementItem: transaction,
        candidates: [],
        selectedAccountId: transaction.matchedAccountId || null,
      }
      this.$bvModal.show('modal-manual-reconciliation')
      const token = this.$store.state.auth.currentUser.token
      this.isLoading = true
      try {
        const response = await ContaPagarReceber.listBankReconciliationCandidates(
          token,
          transaction.id
        )
        const candidates = response.data.candidatos || []
        this.manualForm.candidates = candidates
        if (!this.manualForm.selectedAccountId && candidates.length) {
          this.manualForm.selectedAccountId = candidates[0].id
        }
      } catch (error) {
        this.showApiError(error, 'Nao foi possivel carregar os candidatos.')
      } finally {
        this.isLoading = false
      }
    },
    confirmManualReconciliation() {
      const account = this.manualForm.candidates.find(
        (item) => String(item.id) === String(this.manualForm.selectedAccountId)
      )
      this.confirmReconciliation(this.manualForm.statementItem, account)
    },
    openCreateModal(transaction) {
      this.createForm = {
        statementItem: transaction,
        id_cliente: null,
        id_fornecedor: null,
        id_categoria: null,
        id_centro_custo: null,
        descricao: transaction.description,
      }
      this.$bvModal.show('modal-create-reconciliation-account')
    },
    async createAccountFromStatement() {
      const item = this.createForm.statementItem
      if (
        !item ||
        !this.createForm.descricao ||
        !this.createForm.id_categoria ||
        !this.createForm.id_centro_custo
      ) {
        this.$bvToast.toast('Preencha descrição, categoria e centro de custo.', {
          variant: 'warning',
          solid: false,
          noCloseButton: true,
          toaster: 'b-toaster-top-center',
        })
        return
      }
      if (item.type === 'CREDIT' && !this.createForm.id_cliente) {
        this.$bvToast.toast('Selecione o cliente.', {
          variant: 'warning',
          solid: false,
          noCloseButton: true,
          toaster: 'b-toaster-top-center',
        })
        return
      }
      if (item.type === 'DEBIT' && !this.createForm.id_fornecedor) {
        this.$bvToast.toast('Selecione o fornecedor.', {
          variant: 'warning',
          solid: false,
          noCloseButton: true,
          toaster: 'b-toaster-top-center',
        })
        return
      }

      const token = this.$store.state.auth.currentUser.token
      const payload = {
        tipo_operacao: this.operationByTransaction(item),
        descricao: this.createForm.descricao || item.description,
        id_categoria: this.createForm.id_categoria,
        id_centro_custo: this.createForm.id_centro_custo,
      }
      if (item.type === 'CREDIT') payload.id_cliente = this.createForm.id_cliente
      if (item.type === 'DEBIT') payload.id_fornecedor = this.createForm.id_fornecedor

      this.isLoading = true
      try {
        await ContaPagarReceber.createAccountFromBankTransaction(token, item.id, payload)
        this.$bvModal.hide('modal-create-reconciliation-account')
        await this.loadFinancialAccounts(false)
        this.$bvToast.toast('Lançamento criado e conciliado com sucesso.', {
          variant: 'success',
          solid: false,
          noCloseButton: true,
          toaster: 'b-toaster-top-center',
        })
      } catch (error) {
        this.showApiError(error, 'Nao foi possivel criar o lancamento.')
      } finally {
        this.isLoading = false
      }
    },
    async reconcileSuggestedTransactions() {
      if (!this.selectedBankAccountId || !this.startDate || !this.endDate) {
        this.$bvToast.toast('Selecione a conta bancária e o período.', {
          variant: 'warning',
          solid: false,
          noCloseButton: true,
          toaster: 'b-toaster-top-center',
        })
        return
      }
      const token = this.$store.state.auth.currentUser.token
      this.isLoading = true
      try {
        const response = await ContaPagarReceber.reconcileSuggestedBankTransactions(token, {
          id_tipo_conta: this.selectedBankAccountId,
          data_inicio: this.startDate,
          data_fim: this.endDate,
        })
        const data = response.data || {}
        const erros = Array.isArray(data.erros) && data.erros.length
          ? ` Erros: ${data.erros.join(', ')}`
          : ''
        this.$bvToast.toast(
          `${data.conciliadas || 0} conciliada(s), ${data.pendentes || 0} pendente(s).${erros}`,
          {
            variant: 'success',
            solid: false,
            noCloseButton: true,
            toaster: 'b-toaster-top-center',
          }
        )
        await this.loadFinancialAccounts(false)
      } catch (error) {
        this.showApiError(error, 'Nao foi possivel conciliar as sugestoes.')
      } finally {
        this.isLoading = false
      }
    },
    async applyReconciliationFilters() {
      await this.loadFinancialAccounts()
      this.filtersSidebarOpen = false
    },
    async resetReconciliationFilters() {
      this.statusFilter = 'todos'
      this.startDate = moment().startOf('month').format('YYYY-MM-DD')
      this.endDate = moment().endOf('month').format('YYYY-MM-DD')
      await this.loadFinancialAccounts(false)
      this.filtersSidebarOpen = false
    },
    formatCurrency(value) {
      if (value === null || value === undefined) return 'Nenhum correspondente'
      const signal = value > 0 ? '+ ' : value < 0 ? '- ' : ''
      return `${signal}${new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(Math.abs(value))}`
    },
    valueClass(value) {
      if (value === null || value === undefined) return 'is-empty'
      return value < 0 ? 'is-negative' : 'is-positive'
    },
    statusClass(status) {
      return `is-${status.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')}`
    },
    frontStatus(status) {
      const normalized = String(status || 'PENDING').toUpperCase()
      const statusMap = {
        PENDING: 'pending',
        SUGGESTED: 'suggested',
        MATCHED: 'matched',
        IGNORED: 'ignored',
        UNMATCHED: 'unmatched',
      }
      return statusMap[normalized] || 'pending'
    },
    backendStatus(status) {
      const statusMap = {
        pending: 'PENDING',
        suggested: 'SUGGESTED',
        matched: 'MATCHED',
        ignored: 'IGNORED',
        unmatched: 'UNMATCHED',
      }
      return statusMap[status] || status
    },
    reconciliationStatusLabel(status) {
      const labels = {
        suggested: 'Sugerido',
        matched: 'Conciliado',
        unmatched: 'Pendente',
        pending: 'Pendente',
        ignored: 'Ignorado',
      }
      return labels[status] || 'Pendente'
    },
    accountLabel(account) {
      if (!account) return 'Nenhum correspondente'
      return `#${account.id} - ${account.descricao || account.status_label || 'Lançamento'}`
    },
    apiErrorMessage(error, fallback) {
      const data = error && error.response ? error.response.data : null
      if (data && data.aviso && data.aviso.mensagem) return data.aviso.mensagem
      if (data && data.mensagem) return data.mensagem
      if (data && Array.isArray(data.message)) return data.message.join(', ')
      if (data && data.message) return data.message
      return error && error.message ? error.message : fallback
    },
    showApiError(error, fallback) {
      this.$bvToast.toast(this.apiErrorMessage(error, fallback), {
        variant: 'danger',
        solid: false,
        noCloseButton: true,
        toaster: 'b-toaster-top-center',
      })
    },
  },
}
</script>

<template>
  <Layout>
    <div class="bloco bank-page cash-page">
      <div class="cabecalho">
        <div class="row">
          <div class="col-md-7 mb-2">
            <div class="titulo">
              <i class="uil uil-university"></i>
              Conciliação Bancária
            </div>
            <div class="subTitulo">
              Sincronize seus lançamentos contábeis com o extrato bancário em tempo real.
            </div>
          </div>
          <div class="col-md-5">
            <div class="receivable-page-actions bank-actions">
              <input
                ref="statementInput"
                type="file"
                accept=".csv,.txt,.ofx"
                style="display: none"
                @change="handleStatementFile"
              />
              <button
                class="btn btn-light"
                type="button"
                @click="filtersSidebarOpen = true"
              >
                <i class="uil uil-filter"></i>
                Filtros
              </button>
              <button class="btn btn-webreceita" type="button" @click="triggerFileImport">
                <i class="uil uil-file-import"></i>
                Importar Extrato
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <div class="borda"></div>
        </div>
      </div>

      <b-sidebar
        id="bank-filters-sidebar"
        v-model="filtersSidebarOpen"
        title="Filtros"
        right
        shadow
        backdrop
        width="390px"
        body-class="bank-filters-sidebar-body"
      >
        <b-form class="bank-filter-form" @submit.prevent="applyReconciliationFilters">
          <b-form-group label="Conta bancária">
            <b-form-select
              v-model="selectedBankAccountId"
              :options="[{ value: null, text: 'Selecione a conta bancária' }, ...bankAccountOptions]"
            />
          </b-form-group>
          <b-form-group label="Data inicial">
            <b-form-datepicker
              id="datepicker-conciliacao-inicio"
              v-model="startDate"
              class="bank-date-field"
              placeholder="Selecione a data"
              locale="pt-br"
              :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
              reset-button
              label-reset-button="Limpar"
            >
              <template v-slot:button-content>
                <b-icon icon="calendar" variant="primary"></b-icon>
              </template>
            </b-form-datepicker>
          </b-form-group>
          <b-form-group label="Data final">
            <b-form-datepicker
              id="datepicker-conciliacao-fim"
              v-model="endDate"
              class="bank-date-field"
              placeholder="Selecione a data"
              locale="pt-br"
              :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
              reset-button
              label-reset-button="Limpar"
            >
              <template v-slot:button-content>
                <b-icon icon="calendar" variant="primary"></b-icon>
              </template>
            </b-form-datepicker>
          </b-form-group>
          <b-form-group label="Status">
            <b-form-select v-model="statusFilter" :options="statusOptions" />
          </b-form-group>
          <div class="bank-filter-sidebar-actions">
            <button
              class="btn btn-light"
              type="button"
              @click="resetReconciliationFilters"
            >
              Limpar filtros
            </button>
            <button class="btn btn-webreceita" type="submit">
              <i class="uil uil-search"></i>
              Aplicar filtros
            </button>
          </div>
        </b-form>
      </b-sidebar>

      <div class="receivable-dashboard bank-dashboard">
        <section class="receivable-kpi-grid bank-kpi-grid">
          <article class="receivable-kpi bank-kpi-card">
            <span class="bank-kpi-icon is-neutral"><i class="uil uil-wallet"></i></span>
            <div>
              <span>Entradas no extrato</span>
              <strong>{{ formatCurrency(summary.totalStatementCredits) }}</strong>
              <small><i class="uil uil-arrow-down"></i> Créditos importados</small>
            </div>
          </article>
          <article class="receivable-kpi bank-kpi-card">
            <span class="bank-kpi-icon is-positive"><i class="uil uil-money-withdraw"></i></span>
            <div>
              <span>Saídas no extrato</span>
              <strong>{{ formatCurrency(-summary.totalStatementDebits) }}</strong>
              <small><i class="uil uil-arrow-up"></i> Débitos importados</small>
            </div>
          </article>
          <article class="receivable-kpi bank-kpi-card">
            <span class="bank-kpi-icon is-danger"><i class="uil uil-exclamation-triangle"></i></span>
            <div>
              <span>Diferença</span>
              <strong :class="{ 'is-negative': summary.difference < 0 }">
                {{ formatCurrency(summary.difference) }}
              </strong>
              <small :class="{ 'is-negative': summary.pendingItems > 0 }">
                {{ summary.pendingItems }} pendente(s)
              </small>
            </div>
          </article>
        </section>

        <section class="receivable-list-card bank-table-card">
        <div class="bank-card-head">
          <h2><i class="uil uil-exchange"></i> Transações para Conciliar</h2>
        </div>

        <div class="bank-table-wrap">
          <table class="bank-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Descrição</th>
                <th>Valor (ERP)</th>
                <th>Valor (Banco)</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="transaction in filteredTransactions"
                :key="`${transaction.date}-${transaction.description}`"
                :class="{ 'is-highlighted': transaction.highlighted }"
              >
                <td class="bank-date">{{ normalizeDate(transaction.date) }}</td>
                <td>
                  <strong>{{ transaction.description }}</strong>
                  <small>{{ accountLabel(transaction.matchedAccount) }}</small>
                  <small v-if="transaction.score || transaction.reason">
                    {{ transaction.score ? `${transaction.score}%` : '' }}
                    {{ transaction.reason ? `- ${transaction.reason}` : '' }}
                  </small>
                  <em v-if="transaction.document">{{ transaction.document }}</em>
                </td>
                <td>
                  <span :class="['bank-value', valueClass(transaction.erpValue)]">
                    {{ formatCurrency(transaction.erpValue) }}
                  </span>
                </td>
                <td>
                  <span :class="['bank-value', valueClass(transaction.bankValue)]">
                    {{ formatCurrency(transaction.bankValue) }}
                  </span>
                </td>
                <td>
                  <span :class="['bank-status', statusClass(transaction.statusLabel)]">
                    <i class="uil uil-circle"></i>
                    {{ transaction.statusLabel }}
                  </span>
                </td>
                <td>
                  <div class="bank-row-actions">
                    <button
                      v-if="transaction.action === 'confirm'"
                      class="bank-action-button is-solid"
                      type="button"
                      @click="confirmReconciliation(transaction)"
                    >
                      Conciliar
                    </button>
                    <button
                      v-if="transaction.status === 'suggested' || transaction.status === 'pending' || transaction.status === 'unmatched'"
                      class="bank-action-button"
                      type="button"
                      @click="openManualModal(transaction)"
                    >
                      Manual
                    </button>
                    <button
                      v-if="transaction.action === 'create'"
                      class="bank-action-button"
                      type="button"
                      @click="openCreateModal(transaction)"
                    >
                      Criar
                    </button>
                    <button
                      v-if="transaction.status === 'ignored'"
                      class="bank-action-button"
                      type="button"
                      @click="reopenStatementItem(transaction)"
                    >
                      Reabrir
                    </button>
                    <button
                      v-if="transaction.status !== 'matched' && transaction.status !== 'ignored'"
                      class="bank-action-button is-muted"
                      type="button"
                      @click="ignoreStatementItem(transaction)"
                    >
                      Ignorar
                    </button>
                    <span v-if="transaction.status === 'matched'" class="bank-matched-icon">
                      <i class="uil uil-check-circle"></i>
                    </span>
                  </div>
                </td>
              </tr>
              <tr v-if="!filteredTransactions.length">
                <td colspan="6" class="bank-empty-row">
                  Nenhuma transação encontrada para os filtros selecionados.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <footer class="bank-table-footer">
          <span>Mostrando {{ filteredTransactions.length }} de {{ transactions.length }} transações</span>
          <span>{{ summary.matchedItems }} conciliada(s), {{ summary.ignoredItems }} ignorada(s)</span>
        </footer>
        </section>

        <section class="bank-bottom-grid">
          <article class="bank-assistant-card">
            <span class="bank-assistant-icon"><i class="uil uil-robot"></i></span>
            <div>
              <strong>Assistente de Reconciliação</strong>
              <p>
                Encontramos <b>{{ statementItems.filter((item) => item.status === 'suggested').length }} transações</b> com alta probabilidade de
                correspondência automática.
              </p>
            </div>
            <button class="btn btn-webreceita" type="button" @click="reconcileSuggestedTransactions">
              Conciliar sugeridas
              <i class="uil uil-bolt"></i>
            </button>
          </article>

          <article class="bank-task-card">
            <h2><i class="uil uil-clipboard-notes"></i> Status da Tarefa</h2>
            <div class="bank-progress-head">
              <span>Progresso Total</span>
              <strong>{{ progressPercent }}%</strong>
            </div>
            <div class="bank-progress"><span :style="{ width: `${progressPercent}%` }"></span></div>
            <p><i class="is-positive"></i> {{ summary.matchedItems }} Conciliadas</p>
            <p><i class="is-negative"></i> {{ summary.pendingItems }} Pendentes</p>
          </article>
        </section>
      </div>

      <b-modal
        id="modal-manual-reconciliation"
        title="Conciliação manual"
        hide-footer
      >
        <div v-if="manualForm.statementItem">
          <p class="bank-modal-context">
            {{ manualForm.statementItem.description }} -
            {{ formatCurrency(manualForm.statementItem.amount) }}
          </p>
          <b-form-group label="Conta candidata *">
            <b-form-radio-group
              v-model="manualForm.selectedAccountId"
              stacked
            >
              <b-form-radio
                v-for="candidate in manualForm.candidates"
                :key="candidate.id"
                :value="candidate.id"
              >
                <strong>#{{ candidate.id }} - {{ candidate.descricao || 'Lançamento' }}</strong>
                <small>
                  {{ candidate.tipo_operacao }} |
                  {{ formatCurrency(accountSignedValue(candidate)) }}
                  <span v-if="candidate.score"> | {{ candidate.score }}%</span>
                  <span v-if="candidate.motivo"> - {{ candidate.motivo }}</span>
                </small>
              </b-form-radio>
            </b-form-radio-group>
            <div v-if="!manualForm.candidates.length" class="bank-empty-candidates">
              Nenhum candidato encontrado para esta transação.
            </div>
          </b-form-group>
          <div class="bank-modal-actions">
            <button class="btn btn-light" type="button" @click="$bvModal.hide('modal-manual-reconciliation')">
              Cancelar
            </button>
            <button
              class="btn btn-webreceita"
              type="button"
              :disabled="!manualForm.selectedAccountId"
              @click="confirmManualReconciliation"
            >
              Conciliar
            </button>
          </div>
        </div>
      </b-modal>

      <b-modal
        id="modal-create-reconciliation-account"
        title="Criar lançamento pela conciliação"
        hide-footer
      >
        <b-form @submit.prevent="createAccountFromStatement">
          <p v-if="createForm.statementItem" class="bank-modal-context">
            {{ createForm.statementItem.description }} -
            {{ formatCurrency(createForm.statementItem.amount) }}
          </p>
          <b-form-group label="Descrição *">
            <b-form-input
              v-model="createForm.descricao"
              placeholder="Descrição do lançamento"
            />
          </b-form-group>
          <b-form-group
            v-if="createForm.statementItem && createForm.statementItem.type === 'CREDIT'"
            label="Cliente *"
          >
            <v-select
              label="nome"
              :options="produtores"
              :reduce="(item) => item.id_produtor_rural || item.id_cliente || item.id"
              v-model="createForm.id_cliente"
            />
          </b-form-group>
          <b-form-group
            v-if="createForm.statementItem && createForm.statementItem.type === 'DEBIT'"
            label="Fornecedor *"
          >
            <v-select
              label="nome"
              :options="produtores"
              :reduce="(item) => item.id_produtor_rural || item.id_fornecedor || item.id_cliente || item.id"
              v-model="createForm.id_fornecedor"
            />
          </b-form-group>
          <b-form-group label="Categoria *">
            <v-select
              label="descricao"
              :options="categoriasConta"
              :reduce="(item) => item.id"
              v-model="createForm.id_categoria"
            />
          </b-form-group>
          <b-form-group label="Centro de custo *">
            <v-select
              label="descricao"
              :options="centroCusto"
              :reduce="(item) => item.id"
              v-model="createForm.id_centro_custo"
            />
          </b-form-group>
          <div class="bank-modal-actions">
            <button class="btn btn-light" type="button" @click="$bvModal.hide('modal-create-reconciliation-account')">
              Cancelar
            </button>
            <button class="btn btn-webreceita" type="submit">
              Criar e conciliar
            </button>
          </div>
        </b-form>
      </b-modal>
      <loading :active.sync="isLoading" color="#008e75" :can-cancel="false" />
    </div>
  </Layout>
</template>

<style lang="scss">
.bloco.bank-page {
  background: #fff;
  border: 1px solid #e3e8ef;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.05);
  margin-top: 24px;
  padding: 32px;
  position: relative;
}

.bank-page .titulo {
  align-items: center;
  color: #008e75;
  display: flex;
  font-size: 25px;
  font-weight: 800;
  gap: 8px;
  line-height: 1.2;
  margin-bottom: 8px;
}

.bank-page .subTitulo {
  color: #536074;
  font-size: 14px;
}

.bank-page .borda {
  border-bottom: 1px solid #edeff4;
  margin: 15px 0 18px;
}

.receivable-page-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
}

.receivable-page-actions .btn,
.bank-assistant-card .btn {
  align-items: center;
  border-radius: 5px;
  display: inline-flex;
  font-weight: 700;
  gap: 7px;
  height: 42px;
  justify-content: center;
  margin: 0;
  min-width: 130px;
  padding: 0 18px;
}

.receivable-page-actions .btn-light {
  background: #fff;
  border: 1px solid #d8dee8;
  color: #172033;
}

.btn-webreceita {
  background: #008e75;
  border-color: #008e75;
  color: #fff;
}

.receivable-dashboard {
  background: #f5f7fb;
  display: grid;
  gap: 18px;
  margin: 0 -32px -32px;
  padding: 18px 32px 32px;
}

.receivable-kpi-grid {
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.receivable-kpi,
.bank-table-card,
.bank-assistant-card,
.bank-task-card {
  background: #fff;
  border: 1px solid #dfe6ef;
  border-radius: 7px;
}

.bank-modal-context {
  color: #536074;
  font-size: 12px;
  font-weight: 800;
}

.bank-filter-form {
  display: grid;
  gap: 14px;
  padding: 4px 8px 18px;
}

.bank-filters-sidebar-body {
  padding: 18px 20px 84px;
}

.bank-filter-form .form-group {
  margin: 0;
}

.bank-filter-form label {
  color: #172033;
  font-size: 13px;
  font-weight: 800;
}

.bank-filter-form .form-control,
.bank-filter-form .custom-select {
  border: 1px solid #d8dee8;
  border-radius: 5px;
  color: #536074;
  font-weight: 700;
  height: 42px;
  min-height: 42px;
}

.bank-filter-form .form-control:focus,
.bank-filter-form .custom-select:focus {
  border-color: #008e75;
  box-shadow: 0 0 0 3px rgba(0, 142, 117, 0.12);
}

.bank-filter-form .bank-date-field {
  align-items: stretch;
  border: 1px solid #d8dee8;
  border-radius: 5px;
  min-height: 42px;
}

.bank-filter-form .bank-date-field > .btn {
  align-items: center;
  background: #fff;
  border: 0;
  border-right: 1px solid #d8dee8;
  border-radius: 5px 0 0 5px;
  color: #008e75;
  display: inline-flex;
  height: 40px;
  justify-content: center;
  margin: 0;
  min-width: 42px;
  padding: 0;
}

.bank-filter-form .bank-date-field .form-control {
  border: 0;
  border-radius: 0 5px 5px 0;
  height: 40px;
  min-height: 40px;
}

.bank-filter-sidebar-actions {
  align-items: center;
  background: #fff;
  border-top: 1px solid #e7edf4;
  bottom: 0;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  left: 0;
  padding: 14px 20px;
  position: absolute;
  right: 0;
  width: 100%;
}

.bank-filter-sidebar-actions .btn {
  align-items: center;
  border-radius: 5px;
  border-width: 1px;
  display: inline-flex;
  font-weight: 800;
  gap: 7px;
  height: 42px;
  justify-content: center;
  margin: 0;
  min-width: 138px;
  padding: 0 18px;
  white-space: nowrap;
}

.bank-filter-sidebar-actions .btn-light {
  background: #fff;
  border-color: #d8dee8;
  color: #172033;
}

.bank-filter-sidebar-actions .btn-webreceita {
  min-width: 178px;
}

.bank-kpi-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.bank-kpi-card {
  align-items: center;
  display: flex;
  gap: 18px;
  min-height: 82px;
  padding: 16px 18px;
}

.bank-kpi-icon,
.bank-assistant-icon {
  align-items: center;
  border-radius: 8px;
  display: inline-flex;
  flex: 0 0 42px;
  font-size: 22px;
  height: 42px;
  justify-content: center;
  width: 42px;
}

.bank-kpi-icon.is-neutral { background: #eef3f2; color: #536074; }
.bank-kpi-icon.is-positive { background: #e7f8f3; color: #008e75; }
.bank-kpi-icon.is-danger { background: #fff0f2; color: #dc3545; }

.bank-kpi-card span,
.bank-progress-head span {
  color: #536074;
  font-size: 12px;
  font-weight: 800;
}

.bank-kpi-card strong {
  color: #172033;
  display: block;
  font-size: 19px;
  font-weight: 800;
  margin: 4px 0;
}

.bank-kpi-card strong.is-negative,
.bank-kpi-card small.is-negative {
  color: #dc3545;
}

.bank-kpi-card small {
  color: #008e75;
  font-size: 12px;
  font-weight: 700;
}

.bank-card-head {
  align-items: center;
  border-bottom: 1px solid #dfe6ef;
  display: flex;
  justify-content: space-between;
  padding: 18px 22px;
}

.bank-card-head h2,
.bank-task-card h2 {
  align-items: center;
  display: flex;
  gap: 8px;
  margin: 0;
}

.bank-card-head h2 {
  color: #172033;
  font-size: 16px;
  font-weight: 800;
}

.bank-task-card h2 {
  color: #172033;
  font-size: 16px;
  font-weight: 800;
  margin-bottom: 24px;
  text-transform: uppercase;
}

.bank-card-head h2 i,
.bank-task-card h2 i {
  color: #008e75;
}

.bank-card-head label {
  align-items: center;
  color: #536074;
  display: flex;
  font-size: 12px;
  gap: 10px;
  margin: 0;
}

.bank-card-head .custom-select {
  background-color: #fff;
  border-color: #d8dee8;
  font-size: 12px;
  height: 36px;
  min-width: 160px;
}

.bank-table-wrap {
  overflow-x: auto;
}

.bank-table {
  border-collapse: collapse;
  min-width: 900px;
  width: 100%;
}

.bank-table th {
  background: #eef3fb;
  color: #536074;
  font-size: 12px;
  font-weight: 800;
  padding: 16px 28px;
  text-align: left;
}

.bank-table td {
  border-top: 1px solid #e7edf4;
  color: #001b33;
  padding: 20px 28px;
  vertical-align: middle;
}

.bank-table tr.is-highlighted td {
  background: #f1f8f6;
}

.bank-date,
.bank-table td > strong {
  font-weight: 800;
}

.bank-table td small,
.bank-table td em {
  display: block;
  font-size: 11px;
}

.bank-table td small {
  color: #008e75;
  font-weight: 700;
}

.bank-table td em {
  color: #536074;
  font-style: normal;
}

.bank-value {
  font-size: 15px;
  font-weight: 800;
}

.bank-value.is-positive { color: #008e75; }
.bank-value.is-negative { color: #dc3545; }
.bank-value.is-empty {
  color: #b3bbc8;
  display: block;
  font-size: 12px;
  font-style: italic;
  max-width: 112px;
}

.bank-status {
  align-items: center;
  border-radius: 999px;
  display: inline-flex;
  font-size: 11px;
  font-weight: 800;
  gap: 6px;
  padding: 6px 12px;
  text-transform: uppercase;
}

.bank-status i {
  font-size: 8px;
}

.bank-status.is-conciliado {
  background: #e4f6f0;
  color: #008e75;
}

.bank-status.is-sugerido {
  background: #e6f0ef;
  color: #536074;
}

.bank-status.is-pendente {
  background: #fff0f2;
  color: #dc3545;
}

.bank-status.is-ignorado {
  background: #eef3fb;
  color: #536074;
}

.bank-action-button {
  background: #fff;
  border: 1px solid #008e75;
  border-radius: 5px;
  color: #008e75;
  font-size: 11px;
  font-weight: 800;
  min-height: 42px;
  padding: 8px 16px;
}

.bank-action-button.is-solid {
  background: #008e75;
  color: #fff;
}

.bank-action-button.is-muted {
  border-color: #d8dee8;
  color: #536074;
}

.bank-row-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.bank-matched-icon {
  color: #008e75;
  font-size: 20px;
}

.bank-icon-actions {
  display: flex;
  gap: 12px;
}

.bank-icon-actions button {
  background: transparent;
  border: 0;
  color: #172033;
  font-size: 18px;
}

.bank-table-footer {
  align-items: center;
  border-top: 1px solid #dfe6ef;
  color: #536074;
  display: flex;
  justify-content: space-between;
  padding: 18px 28px;
}

.bank-table-footer button {
  background: #fff;
  border: 1px solid #d8dee8;
  border-radius: 5px;
  color: #536074;
  height: 42px;
  width: 42px;
}

.bank-empty-row {
  color: #536074;
  font-weight: 700;
  text-align: center;
}

.bank-bottom-grid {
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(0, 1fr) 240px;
}

.bank-assistant-card {
  align-items: center;
  display: grid;
  gap: 18px;
  grid-template-columns: auto 1fr 240px;
  min-height: 150px;
  padding: 24px;
}

.bank-assistant-icon {
  background: #e4f6f0;
  color: #008e75;
}

.bank-assistant-card strong {
  color: #172033;
  display: block;
  font-size: 16px;
  margin-bottom: 4px;
}

.bank-assistant-card p {
  color: #536074;
  margin: 0;
}

.bank-assistant-card b {
  color: #008e75;
}

.bank-assistant-card .btn {
  height: 86px;
  white-space: normal;
}

.bank-task-card {
  padding: 26px;
}

.bank-progress-head {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.bank-progress-head strong {
  color: #008e75;
}

.bank-progress {
  background: #d7dedb;
  border-radius: 999px;
  height: 8px;
  margin-bottom: 20px;
  overflow: hidden;
}

.bank-progress span {
  background: #008e75;
  display: block;
  height: 100%;
  width: 82%;
}

.bank-task-card p {
  align-items: center;
  color: #536074;
  display: flex;
  gap: 8px;
  margin: 0 0 14px;
}

.bank-task-card p i {
  border-radius: 50%;
  display: inline-block;
  height: 8px;
  width: 8px;
}

.bank-task-card p i.is-positive { background: #008e75; }
.bank-task-card p i.is-negative { background: #dc3545; }

.bank-modal-context {
  background: #f5f7fb;
  border-radius: 5px;
  margin-bottom: 14px;
  padding: 10px 12px;
}

.bank-empty-candidates {
  background: #f5f7fb;
  border-radius: 5px;
  color: #536074;
  font-weight: 700;
  padding: 12px;
  text-align: center;
}

.custom-radio {
  margin-bottom: 10px;
}

.custom-radio strong,
.custom-radio small {
  display: block;
}

.custom-radio small {
  color: #536074;
  font-size: 12px;
  margin-top: 2px;
}

.bank-modal-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 18px;
}

@media (max-width: 1199px) {
  .bank-kpi-grid,
  .bank-bottom-grid,
  .bank-assistant-card {
    grid-template-columns: 1fr;
  }

  .bank-assistant-card .btn {
    height: 48px;
  }
}

@media (max-width: 767px) {
  .receivable-page-actions,
  .bank-card-head,
  .bank-table-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .receivable-page-actions .btn,
  .bank-card-head label,
  .bank-card-head .custom-select {
    width: 100%;
  }
}
</style>
